import type { TrainingOption } from '@hcc/types';

/**
 * Categorize answer: for each item, tap which category it belongs in.
 * Tap-to-assign rather than drag-and-drop so it works cleanly on tablets and
 * with assistive tech. Controlled; the value maps item id to chosen category
 * id. Knows nothing about correctness (graded server-side).
 */
export function CategorizeAnswer({
  categories,
  items,
  value,
  onChange,
  disabled,
}: {
  categories: TrainingOption[];
  items: TrainingOption[];
  value: Record<string, string> | undefined;
  onChange: (next: Record<string, string>) => void;
  disabled?: boolean;
}) {
  const placed = value ?? {};
  return (
    <div className="flex flex-col gap-3">
      {items.map((item) => (
        <div key={item.id} className="rounded-token-sm border border-line/10 bg-surface p-3">
          <div className="mb-2 text-sm font-medium text-fg">{item.label}</div>
          <div
            role="radiogroup"
            aria-label={`Category for ${item.label}`}
            className="flex flex-wrap gap-2"
          >
            {categories.map((cat) => {
              const selected = placed[item.id] === cat.id;
              return (
                <button
                  key={cat.id}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  disabled={disabled}
                  onClick={() => onChange({ ...placed, [item.id]: cat.id })}
                  className={`min-h-[40px] rounded-token-sm border px-3 py-1.5 text-sm transition-colors disabled:opacity-60 ${
                    selected
                      ? 'border-accent bg-accent/10 text-fg'
                      : 'border-line-strong/20 bg-surface text-fg-muted hover:bg-surface-2'
                  }`}
                >
                  {cat.label}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
